// @oasis-permission-inherits: /capcode (capcode:view)
import { Link, createFileRoute } from "@tanstack/react-router";
import { CalendarClock, Pause, Play, Plus } from "lucide-react";
import * as React from "react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { fetchAutomations, pauseAutomation, resumeAutomation } from "~/modules/cap-code/network";
import type { AutomationRecord } from "~/modules/cap-code/schemas";

export const Route = createFileRoute("/_dash/capcode/automations/")({
  component: AutomationsRoute,
});

function formatDate(timestamp: number | null): string {
  return timestamp ? new Date(timestamp).toLocaleString() : "Not scheduled";
}

function statusClass(status: AutomationRecord["status"]): string {
  if (status === "active") {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }
  if (status === "draft") {
    return "border-amber-200 bg-amber-50 text-amber-700";
  }
  return "border-slate-200 bg-slate-50 text-slate-700";
}

function AutomationsRoute() {
  const [automations, setAutomations] = React.useState<AutomationRecord[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [busyId, setBusyId] = React.useState<string | null>(null);

  const loadAutomations = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setAutomations(await fetchAutomations());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load automations");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadAutomations();
  }, [loadAutomations]);

  const toggleStatus = async (automation: AutomationRecord) => {
    if (automation.status === "draft") {
      return;
    }
    setBusyId(automation.id);
    setError(null);
    try {
      if (automation.status === "active") {
        await pauseAutomation(automation.id);
      } else {
        await resumeAutomation(automation.id);
      }
      await loadAutomations();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update automation");
    } finally {
      setBusyId(null);
    }
  };

  const empty = !loading && automations.length === 0;

  return (
    <div className="h-[calc(100dvh-57px)] overflow-y-auto bg-background">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-5 px-4 py-6">
        <header className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Automations</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Scheduled Cap Code prompts with hidden run conversations.
            </p>
          </div>
          <div className="flex gap-2">
            <Button asChild>
              <Link to="/capcode/automations/new">
                <Plus size={16} />
                New
              </Link>
            </Button>
          </div>
        </header>

        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {loading && automations.length === 0 && (
          <div className="rounded-lg border border-border bg-white p-8 text-sm text-muted-foreground">
            Loading automations...
          </div>
        )}

        {empty && (
          <div className="rounded-lg border border-border bg-white p-10 text-center">
            <CalendarClock className="mx-auto text-muted-foreground" size={28} />
            <h2 className="mt-3 text-sm font-semibold">No automations yet</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Create one to run a Cap Code prompt on a recurring schedule.
            </p>
          </div>
        )}

        {automations.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-border bg-white">
            {automations.map((automation) => (
              <div
                key={automation.id}
                className="flex items-center gap-4 border-b border-border p-4 last:border-b-0"
              >
                <Link
                  to="/capcode/automations/$automationId"
                  params={{ automationId: automation.id }}
                  className="min-w-0 flex-1"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <h2 className="truncate text-sm font-semibold">{automation.title}</h2>
                    <Badge className={statusClass(automation.status)}>{automation.status}</Badge>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span>{automation.scheduleSummary}</span>
                    <span>Next: {formatDate(automation.nextRunAt)}</span>
                    <span>Last: {formatDate(automation.lastRunAt)}</span>
                  </div>
                  <p className="mt-2 line-clamp-1 text-sm text-muted-foreground">
                    {automation.prompt}
                  </p>
                </Link>
                {automation.status !== "draft" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void toggleStatus(automation)}
                    disabled={busyId === automation.id}
                  >
                    {automation.status === "active" ? <Pause size={15} /> : <Play size={15} />}
                    {automation.status === "active" ? "Pause" : "Resume"}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
